import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '@/auth/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';

/** SPEC §8 — status booleans plus the two live connection tests. No secrets are ever shown. */
export function ConnectionsSettingsPage(): React.JSX.Element {
  const { can } = useAuth();
  // Live connection tests hit the providers, so they follow settings.manage (SPEC §9).
  const canTest = can('settings.manage');
  const status = useQuery({ queryKey: ['settings', 'status'], queryFn: () => api.settings.status() });

  const testElevenLabs = useMutation({
    mutationFn: () => api.settings.testElevenLabs(),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `ElevenLabs OK — ${result.tier ?? 'unknown tier'}, ${result.remainingCredits ?? '?'} credits left`,
        );
      } else {
        toast.error(`ElevenLabs failed: ${result.error ?? 'unknown error'}`);
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const testGcs = useMutation({
    mutationFn: () => api.settings.testGcs(),
    onSuccess: (result) => {
      if (result.ok) toast.success(`GCS OK — wrote and deleted ${result.path ?? ''}`);
      else toast.error(`GCS failed: ${result.error ?? 'unknown error'}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const flag = (value: boolean): React.JSX.Element =>
    value ? (
      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
    ) : (
      <XCircle className="h-4 w-4 text-destructive" />
    );

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>ElevenLabs</CardTitle>
          <CardDescription>API key comes from the environment and is never stored in the database.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {status.data ? (
            <>
              <div className="flex items-center gap-2">
                {flag(status.data.elevenLabs.apiKeyConfigured)} API key configured
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">tier {status.data.elevenLabs.planTier}</Badge>
                <Badge variant="secondary">
                  safe concurrency {status.data.elevenLabs.safeConcurrency}
                </Badge>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Loading…</p>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!canTest || testElevenLabs.isPending}
            onClick={() => testElevenLabs.mutate()}
          >
            {testElevenLabs.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Google Cloud Storage</CardTitle>
          <CardDescription>Shared public bucket — the app never touches bucket-level config.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {status.data ? (
            <>
              <div className="flex items-center gap-2">
                {flag(status.data.gcs.credentialsConfigured)} Service account configured
              </div>
              <div className="flex items-center gap-2">
                {flag(status.data.gcs.bucketConfigured)} Bucket configured
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">bucket {status.data.gcs.bucket}</Badge>
                <Badge variant="secondary">prefix {status.data.gcs.prefix}</Badge>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Loading…</p>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!canTest || testGcs.isPending}
            onClick={() => testGcs.mutate()}
          >
            {testGcs.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Readiness</CardTitle>
          <CardDescription>
            Generation is blocked until a voice is selected for both languages (SPEC §8).
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 text-sm">
          {status.data ? (
            <>
              <Badge variant={status.data.voices.enSelected ? 'success' : 'warning'}>
                EN voice {status.data.voices.enSelected ? 'ready' : 'missing'}
              </Badge>
              <Badge variant={status.data.voices.arSelected ? 'success' : 'warning'}>
                AR voice {status.data.voices.arSelected ? 'ready' : 'missing'}
              </Badge>
              <Badge variant={status.data.worker.enabled ? 'success' : 'secondary'}>
                worker {status.data.worker.enabled ? 'enabled' : 'disabled'}
              </Badge>
              <Badge variant={status.data.readyToGenerate ? 'success' : 'warning'}>
                {status.data.readyToGenerate ? 'ready to generate' : 'not ready'}
              </Badge>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
